import { useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { Expand } from "lucide-react";
import { useLang } from "@/lib/i18n";

const SPACING = 900;
const POS = [[-20, -3], [19, 5], [-14, 7], [22, -6], [-24, 4], [15, -7]];

const useNarrow = () => typeof window !== "undefined" && window.innerWidth < 768;

const Item = ({ p, i, total, progress, narrow, active, onOpen }) => {
  const [x, y] = POS[i % POS.length];
  const rel = useTransform(progress, (v) => i - v * Math.max(total - 1, 0));
  const transform = useTransform(rel, (r) => `translate3d(calc(-50% + ${narrow ? x * 0.3 : x}vw), calc(-50% + ${narrow ? y * 0.6 : y}vh), ${-r * SPACING}px)`);
  const opacity = useTransform(rel, [-0.45, -0.12, 0, 2.6, 3.6], [0, 1, 1, 1, 0]);
  const blur = useTransform(rel, [0, 0.6, 3], ["blur(0px)", "blur(1px)", "blur(3px)"]);
  const { t } = useLang();
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      tabIndex={active ? 0 : -1}
      className={`tn-item ${active ? "tn-item-on" : ""}`}
      style={{ transform, opacity, filter: blur, zIndex: total - i }}
      aria-label={`${t("photo.zoom")} ${i + 1}`}
      data-testid={`tunnel-photo-${p.id}`}
    >
      <img src={p.src} alt={p.title || p.description || t("col.photoAlt")} loading={i < 4 ? "eager" : "lazy"} />
      <span className="tn-item-zoom"><Expand size={14} /></span>
    </motion.button>
  );
};

const Ring = ({ k, rings, progress, total }) => {
  const span = rings * (SPACING / 2);
  const z = useTransform(progress, (v) => {
    const cam = v * Math.max(total - 1, 0) * SPACING;
    return -((((k * SPACING) / 2 - cam) % span) + span) % span;
  });
  const transform = useTransform(z, (d) => `translate3d(-50%, -50%, ${d}px)`);
  const opacity = useTransform(z, [-span, -span * 0.6, -200, 0], [0, 0.9, 0.9, 0]);
  return <motion.span className="tn-ring" style={{ transform, opacity }} aria-hidden />;
};

const Hud = ({ p, i, total }) => {
  const facts = [p.number && `No. ${p.number}`, p.country, p.language].filter(Boolean);
  const { t } = useLang();
  return (
  <div className="tn-hud" data-testid="tunnel-hud">
    <div className="mono flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
      <span data-testid="tunnel-counter">{String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
      <span className="h-px w-8 bg-white/25" />
      <span>{t(`col.${p.category}`)}</span>
    </div>
    {p.title && <p className="font-display mt-3 text-2xl leading-tight md:text-3xl" data-testid="tunnel-title">{p.title}</p>}
    <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-2">
      <span className="font-display text-5xl leading-none md:text-6xl" data-testid="tunnel-year">{p.year ?? "—"}</span>
      {p.universe && <span className="tn-tag" data-testid="tunnel-universe">{p.universe}</span>}
    </div>
    {facts.length > 0 && <p className="mono mt-3 text-[11px] uppercase tracking-[0.16em] text-[#cfc7b9]" data-testid="tunnel-facts">{facts.join(" · ")}</p>}
    {(p.description || !p.title) && <p className="mt-3 max-w-sm text-sm leading-relaxed text-[#cfc7b9]" data-testid="tunnel-description">{p.description || t("col.onePiece")}</p>}
  </div>
  );
};

export const Tunnel = ({ photos, onOpen }) => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const narrow = useNarrow();
  const total = photos.length;
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => setActive(Math.min(total - 1, Math.max(0, Math.round(v * (total - 1))))));
  const p = photos[Math.min(active, total - 1)];
  return (
    <section ref={ref} className="relative" style={{ height: `${100 + Math.max(total - 1, 0) * 80}vh` }} data-testid="collection-tunnel">
      <div className="tn-stage">
        <div className="tn-glow" aria-hidden />
        <div className="tn-world">
          {Array.from({ length: 8 }, (_, k) => <Ring key={k} k={k} rings={8} progress={scrollYProgress} total={total} />)}
          {photos.map((ph, i) => <Item key={ph.id} p={ph} i={i} total={total} progress={scrollYProgress} narrow={narrow} active={i === active} onOpen={() => onOpen(i)} />)}
        </div>
        {p && <Hud p={p} i={active} total={total} />}
        <div className="tn-progress" aria-hidden><motion.span style={{ scaleY: scrollYProgress }} /></div>
        {total > 1 && <p className="mono absolute right-5 top-[88px] text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] md:right-10">{t("col.scroll")}</p>}
      </div>
    </section>
  );
};
